(() => {
  'use strict';
  if (customElements.get('jb-product-description')) return;
  class JBProductDescription extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle || !this.hasAttribute('data-collapsible')) return;
      this.viewport = this.querySelector('.jb-description__viewport');
      this.preview = this.querySelector('[data-description-preview]');
      this.excerpt = this.querySelector('[data-description-excerpt]');
      this.full = this.querySelector('[data-description-full]');
      this.more = this.querySelector('[data-description-open]');
      this.less = this.querySelector('[data-description-close]');
      this.copy = this.querySelector('[data-description-copy]');
      if (!this.viewport || !this.preview || !this.excerpt || !this.full || !this.more || !this.less || !this.copy) return;
      this.lifecycle = new AbortController();
      this.text = (this.excerpt.dataset.previewText || this.excerpt.textContent).replace(/\s+/g, ' ').trim();
      this.expanded = false;
      this.preview.hidden = false;
      this.full.hidden = true;
      this.full.inert = true;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const walker = document.createTreeWalker(this.copy, NodeFilter.SHOW_TEXT);
      let node;
      let lastText;
      while ((node = walker.nextNode())) { if (node.textContent.trim()) lastText = node; }
      const last = lastText?.parentElement.closest('p, li');
      if (last && this.copy.contains(last)) last.append(' ', this.less);
      const options = { signal: this.lifecycle.signal };
      this.more.addEventListener('click', () => this.toggle(true), options);
      this.less.addEventListener('click', () => this.toggle(false), options);
      this.fit();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(entries => {
          const width = entries[0].contentRect.width;
          if (Math.abs(width - (this.lastWidth || 0)) < 1) return;
          this.lastWidth = width;
          if (this.busy) this.finish?.();
          this.fit();
        });
        this.resizeObserver.observe(this);
      }
      document.fonts?.ready.then(() => { if (this.isConnected) this.fit(); });
    }

    fit() {
      if (!this.excerpt || this.clientWidth === 0 || this.busy) return;
      const wasHidden = this.preview.hidden;
      this.preview.classList.add('is-measuring');
      this.preview.hidden = false;
      const limit = parseFloat(getComputedStyle(this.preview).lineHeight) * 2 + 1;
      const words = this.text.split(' ');
      let low = 1;
      let high = words.length;
      let best = 1;
      while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        this.excerpt.textContent = `${words.slice(0, middle).join(' ')}...`;
        if (this.preview.getBoundingClientRect().height <= limit) {
          best = middle;
          low = middle + 1;
        } else high = middle - 1;
      }
      this.excerpt.textContent = `${words.slice(0, best).join(' ').replace(/[.,;:!?]+$/, '')}...`;
      this.preview.hidden = wasHidden;
      this.preview.classList.remove('is-measuring');
      this.dataset.descriptionReady = 'true';
    }

    toggle(expand) {
      if (this.busy || this.expanded === expand) return;
      this.busy = true;
      const outgoing = expand ? this.preview : this.full;
      const incoming = expand ? this.full : this.preview;
      const control = expand ? this.less : this.more;
      const focused = this.contains(document.activeElement);
      const start = this.viewport.getBoundingClientRect().height;
      this.viewport.style.height = `${start}px`;
      this.viewport.classList.add('is-transitioning');
      outgoing.classList.add('is-layer');
      incoming.classList.add('is-layer');
      incoming.hidden = false;
      incoming.inert = false;
      outgoing.inert = true;
      const end = incoming.getBoundingClientRect().height;
      this.expanded = expand;
      this.more.setAttribute('aria-expanded', String(expand));
      this.less.setAttribute('aria-expanded', String(expand));
      this.dataset.expanded = String(expand);
      if (focused) control.focus({ preventScroll: true });
      let complete = false;
      this.finish = () => {
        if (complete) return;
        complete = true;
        outgoing.hidden = true;
        outgoing.classList.remove('is-layer');
        incoming.classList.remove('is-layer');
        this.animations?.forEach(animation => animation.cancel());
        this.animations = [];
        this.viewport.style.removeProperty('height');
        this.viewport.classList.remove('is-transitioning');
        this.busy = false;
        if (!expand && focused && this.getBoundingClientRect().bottom < 100) {
          this.scrollIntoView({ block: 'start', behavior: this.reduced.matches ? 'auto' : 'smooth' });
        }
      };
      if (this.reduced.matches || !this.viewport.animate) { this.finish(); return; }
      const timing = { duration: 220, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'both' };
      this.animations = [
        this.viewport.animate([{ height: `${start}px` }, { height: `${end}px` }], timing),
        outgoing.animate([{ opacity: 1 }, { opacity: 0 }], timing),
        incoming.animate([{ opacity: 0 }, { opacity: 1 }], timing)
      ];
      this.animations[0].finished.then(this.finish, this.finish);
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.finish?.();
        this.resizeObserver?.disconnect();
        this.lifecycle?.abort();
        this.lifecycle = null;
      });
    }
  }
  customElements.define('jb-product-description', JBProductDescription);
})();

/* Just Brilliant: keep the mobile purchase action synchronized with Shopify. */
(() => {
  'use strict';
  function init(root) {
    if (root.dataset.experienceReady) return;
    root.dataset.experienceReady = 'true';
    const lifecycle = new AbortController();
    const options = { signal: lifecycle.signal };
    root.querySelector('.jb-product-details-link')?.addEventListener('click', event => {
      const details = root.querySelector('.jb-product-description details');
      if (!details) return;
      event.preventDefault();
      details.open = true;
      details.querySelector('summary')?.focus({ preventScroll: true });
      details.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }, options);
    root.querySelectorAll('.product-single__media-zoom').forEach(link => link.tabIndex = 0);
    const informationPanel = root.querySelector('.product-information');
    function sizeInformationPanel() {
      if (!informationPanel) return;
      const top = Math.min(90, window.innerHeight - informationPanel.offsetHeight - 24);
      informationPanel.style.setProperty('--jb-info-top', `${top}px`);
    }
    const panelObserver = typeof ResizeObserver === 'function' && informationPanel ? new ResizeObserver(sizeInformationPanel) : null;
    panelObserver?.observe(informationPanel);
    window.addEventListener('resize', sizeInformationPanel, { passive: true });
    sizeInformationPanel();
    root.addEventListener('jb:experience-dispose', () => {
      panelObserver?.disconnect();
      window.removeEventListener('resize', sizeInformationPanel);
      lifecycle.abort();
      delete root.dataset.experienceReady;
    }, { once: true });
    const bar = root.querySelector('.jb-purchase-bar');
    if (!bar) return;
    const proxy = bar.querySelector('button');
    const price = bar.querySelector('.jb-purchase-bar__price');
    if (!proxy || !price) return;
    const info = root.querySelector('.product-information--inner');
    const title = root.querySelector('.product-title');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let queued = 0;
    let disposed = false;
    const source = () => {
      const request = root.querySelector('.jb-request-price-button');
      return request && request.getClientRects().length ? request : root.querySelector('.single-add-to-cart-button');
    };
    function hideBar() {
      bar.hidden = true;
      bar.inert = true;
      bar.classList.remove('is-visible');
      bar.setAttribute('aria-hidden', 'true');
    }
    function sync() {
      queued = 0;
      if (disposed) return;
      if (window.innerWidth >= 768) { hideBar(); return; }
      const action = source();
      if (!action || !title) { hideBar(); return; }
      const priceSource = root.querySelector('.cus_prd_pric_wrp');
      const priceText = priceSource?.innerText.trim() || '';
      if (price.textContent !== priceText) price.textContent = priceText;
      const label = action.innerText.trim();
      if (proxy.textContent !== label) proxy.textContent = label;
      const purchaseForm = action.closest('form');
      const invalid = purchaseForm && Array.from(purchaseForm.elements).some(control => control.willValidate && !control.validity.valid);
      proxy.disabled = action.disabled || action.getAttribute('aria-disabled') === 'true' || Boolean(invalid);
      const box = action.getBoundingClientRect();
      const onScreen = box.top >= 0 && box.bottom <= window.innerHeight;
      const footer = document.querySelector('.shopify-section-group-footer-group, .cus_jstbr_ftr_section, footer');
      const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
      const overlayOpen = document.querySelector('[role="dialog"][aria-hidden="false"], dialog[open]');
      const header = document.querySelector('#header');
      const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
      const gallery = root.querySelector('.product-image-container');
      const galleryBox = gallery?.getBoundingClientRect();
      const galleryVisible = galleryBox && galleryBox.bottom > headerBottom && galleryBox.top < window.innerHeight;
      const related = document.querySelector('.jb-related-products');
      const relatedBox = related?.getBoundingClientRect();
      const relatedVisible = relatedBox && relatedBox.height > 0 && relatedBox.bottom > headerBottom && relatedBox.top < window.innerHeight;
      const servicesBox = document.querySelector('.jb-pdp-services--features')?.getBoundingClientRect();
      const servicesVisible = servicesBox && servicesBox.bottom > headerBottom && servicesBox.top < window.innerHeight;
      const withinProduct = root.getBoundingClientRect().bottom > headerBottom;
      const titleBox = title.getBoundingClientRect();
      bar.classList.toggle('has-title', titleBox.top >= window.innerHeight);
      const editing = document.activeElement?.matches('input:not([type=radio]):not([type=checkbox]):not([type=submit]):not([type=button]), textarea, [contenteditable="true"]');
      const show = !editing && withinProduct && !servicesVisible && (galleryVisible || titleBox.bottom < headerBottom) && !onScreen && !footerVisible && !relatedVisible && !overlayOpen;
      bar.hidden = false;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
      bar.setAttribute('aria-hidden', String(!show));
    }
    function schedule() {
      if (queued || disposed) return;
      queued = requestAnimationFrame(sync);
    }
    proxy.addEventListener('click', () => {
      const action = source();
      if (!action || proxy.disabled) return;
      const form = action.closest('form');
      if (form && !form.reportValidity()) {
        form.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      action.click();
    }, options);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    const observer = new MutationObserver(schedule);
    if (info) observer.observe(info, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
    document.addEventListener('product:variant-change', schedule);
    root.addEventListener('change', schedule, options);
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    window.visualViewport?.addEventListener('resize', schedule, { passive: true });
    ['jb:menu-open', 'jb:menu-close', 'jb:contact-open', 'jb:search-open', 'panel:open', 'panel:closed'].forEach(name => document.addEventListener(name, schedule));
    root.addEventListener('jb:experience-dispose', () => {
      disposed = true;
      cancelAnimationFrame(queued);
      observer.disconnect();
      root.removeEventListener('change', schedule);
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('product:variant-change', schedule);
      ['jb:menu-open', 'jb:menu-close', 'jb:contact-open', 'jb:search-open', 'panel:open', 'panel:closed'].forEach(name => document.removeEventListener(name, schedule));
    }, { once: true });
    sync();
  }
  function initialize() { document.querySelectorAll('.jb-product-experience').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('jb:product-replaced', initialize);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.jb-product-experience').forEach(root => root.dispatchEvent(new Event('jb:experience-dispose')));
  });
})();

